import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/layout";
import { SEOHead } from "@/components/seo";
import { Button } from "@/components/ui/button";
import { Home, ArrowRight } from "lucide-react";
import { useAgentBranding } from "@/hooks/useAgentBranding";
import AgentBrandingCard from "@/components/agent/AgentBrandingCard";

const PAGE_TITLE = "First Time Home Buyer in NJ (2026) | Programs, Grants & Loans Guide | Prime Realty Partners";
const PAGE_DESC = "Complete guide for first time home buyers in New Jersey. NJ programs, down payment assistance, NJHMFA grants, FHA loans, and closing costs explained. Prime Realty Partners.";
const CANONICAL = "https://www.prpsj.com/first-time-home-buyer-nj";

const faqs = [
  {
    q: "How much do I need for a down payment to buy a house in NJ?",
    a: "Down payments in NJ range from 0% (VA and USDA loans) to 3% (Conventional 97) to 3.5% (FHA) to 20% (to avoid PMI). Many first time buyers put down 3-5%. The NJHMFA program offers up to $15,000 in down payment assistance.",
  },
  {
    q: "What credit score do I need to buy a house in NJ?",
    a: "Most conventional loans require a 620+ credit score. FHA loans accept 580+ with 3.5% down. VA loans have no official minimum but most lenders want 620+. The higher your score, the better your interest rate.",
  },
  {
    q: "Do first time home buyers in NJ pay closing costs?",
    a: "Yes, buyers typically pay 2-5% of the purchase price in closing costs. However, you can negotiate for the seller to contribute toward closing costs, and the NJHMFA down payment assistance program can help cover these costs.",
  },
  {
    q: "How long does it take to buy a house in NJ?",
    a: "From starting your search to closing, the typical timeline is 2-4 months. Once your offer is accepted, closing usually takes 30-45 days. Attorney review takes 3 business days, and the inspection is typically done within 7-10 days of the accepted offer.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const webPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "First Time Home Buyer Guide for New Jersey (2026)",
  description: PAGE_DESC,
  url: CANONICAL,
};

const bestTowns = [
  { name: "Delran", slug: "delran-homes-for-sale", desc: "Median prices in the low $300s with good schools and easy commuter access." },
  { name: "Maple Shade", slug: "maple-shade-homes-for-sale", desc: "Affordable homes near Cherry Hill and Moorestown with a small-town feel." },
  { name: "Bellmawr", slug: "bellmawr-homes-for-sale", desc: "One of Camden County's most affordable communities with highway access." },
  { name: "Pennsauken", slug: "pennsauken-homes-for-sale", desc: "Diverse, affordable community minutes from Philadelphia." },
  { name: "Willingboro", slug: "willingboro-homes-for-sale", desc: "Spacious homes at great prices in Burlington County." },
  { name: "Clementon", slug: "clementon-homes-for-sale", desc: "Lakeside community with some of the lowest prices in Camden County." },
  { name: "Gloucester Township", slug: "gloucester-township-homes-for-sale", desc: "Large municipality with varied housing options across price ranges." },
];

const FirstTimeHomeBuyer = () => {
  useEffect(() => {
    document.title = PAGE_TITLE;
  }, []);

  const { brandedAgent, isBranded } = useAgentBranding();

  return (
    <Layout>
      <SEOHead
        title={PAGE_TITLE}
        description={PAGE_DESC}
        canonicalUrl={CANONICAL}
        schema={[faqSchema, webPageSchema]}
        keywords="first time home buyer NJ, NJ first time buyer programs, NJHMFA, down payment assistance NJ, FHA loans NJ, buying first home New Jersey"
        noindex={isBranded}
        breadcrumbs={[{ name: "Home", url: "https://www.prpsj.com" }, { name: "First Time Buyer Guide", url: CANONICAL }]}
      />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-primary/10 to-primary/5 py-20">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Home className="h-4 w-4" />
              Buyer's Guide
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
              First Time Home Buyer Guide for New Jersey (2026)
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Everything you need to know about buying your first home in South Jersey — programs, grants, loans, and step-by-step guidance.
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <article className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 space-y-16">

          {/* Section 1 */}
          <section>
            <h2 className="font-heading text-3xl font-bold mb-6">Steps to Buying Your First Home in NJ</h2>
            <p className="text-muted-foreground mb-6">Buying your first home in New Jersey can feel overwhelming, but it doesn't have to be. Here's the process broken down into clear steps.</p>

            <div className="space-y-6">
              {[
                { title: "Step 1: Check Your Credit and Finances", text: "Before you start looking at homes, know where you stand financially. Most lenders want to see a credit score of 620 or higher for conventional loans, though FHA loans accept scores as low as 580. Pull your credit report, pay down high-interest debt, and save for your down payment and closing costs." },
                { title: "Step 2: Get Pre-Approved for a Mortgage", text: "A pre-approval letter from a lender shows sellers you're serious and tells you exactly how much you can afford. Your lender will review your income, debt, assets, and credit history. In South Jersey, we recommend working with a local lender who understands our market — they can often close faster and navigate NJ-specific programs." },
                { title: "Step 3: Find a Real Estate Agent", text: "Working with a local agent who knows South Jersey saves you time and money. Your agent will help you find properties, negotiate offers, navigate inspections, and guide you through closing. In New Jersey, buyers typically don't pay their agent's commission directly — it's factored into the transaction." },
                { title: "Step 4: Start Your Home Search", text: "With your pre-approval and agent in place, you can start touring homes. Focus on your must-haves (bedrooms, location, school district) and be ready to move quickly in competitive markets like Cherry Hill, Moorestown, and Haddonfield." },
                { title: "Step 5: Make an Offer and Negotiate", text: "When you find the right home, your agent will help you craft a competitive offer based on comparable sales and market conditions. In NJ, offers typically include an attorney review period of 3 business days where either side can modify or cancel the contract." },
                { title: "Step 6: Home Inspection and Attorney Review", text: "Once your offer is accepted, you'll hire a home inspector to evaluate the property. Your real estate attorney reviews the contract during the attorney review period. This is standard in NJ — every buyer and seller should have their own attorney." },
                { title: "Step 7: Secure Your Mortgage and Close", text: "Your lender will order an appraisal, finalize your loan, and prepare for closing. In NJ, closings typically happen 30-45 days after your offer is accepted. At closing, you'll sign documents, pay your closing costs, and get the keys to your new home." },
              ].map((step, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold flex-shrink-0 text-sm">
                    {i + 1}
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-bold mb-1">{step.title}</h3>
                    <p className="text-muted-foreground">{step.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="font-heading text-3xl font-bold mb-6">NJ First Time Home Buyer Programs</h2>
            <p className="text-muted-foreground mb-6">New Jersey offers several programs specifically designed to help first time buyers get into their first home with lower costs.</p>

            <div className="space-y-6">
              <div>
                <h3 className="font-heading text-xl font-bold mb-2">NJHMFA First Time Home Buyer Program</h3>
                <p className="text-muted-foreground">The New Jersey Housing and Mortgage Finance Agency offers below-market interest rate mortgages for first time buyers. You must meet income limits and purchase a home within NJ price limits. This program can be combined with down payment assistance.</p>
              </div>
              <div>
                <h3 className="font-heading text-xl font-bold mb-2">NJHMFA Down Payment Assistance Program</h3>
                <p className="text-muted-foreground">Eligible first time buyers can receive up to $15,000 in down payment and closing cost assistance through a forgivable second mortgage. The loan is forgiven after 5 years of living in the home as your primary residence.</p>
              </div>
              <div>
                <h3 className="font-heading text-xl font-bold mb-2">FHA Loans</h3>
                <p className="text-muted-foreground">Federal Housing Administration loans allow down payments as low as 3.5% with credit scores of 580 or higher. FHA loans are popular with first time buyers because of their lower requirements, though they do require mortgage insurance premiums.</p>
              </div>
              <div>
                <h3 className="font-heading text-xl font-bold mb-2">VA Loans</h3>
                <p className="text-muted-foreground">If you're a veteran or active military, VA loans offer zero down payment and no private mortgage insurance. This is one of the best mortgage programs available.</p>
              </div>
              <div>
                <h3 className="font-heading text-xl font-bold mb-2">USDA Loans</h3>
                <p className="text-muted-foreground">For homes in eligible rural areas of South Jersey (parts of Burlington and Gloucester counties qualify), USDA loans offer zero down payment for buyers who meet income limits.</p>
              </div>
              <div>
                <h3 className="font-heading text-xl font-bold mb-2">Conventional 97 Loans</h3>
                <p className="text-muted-foreground">Some conventional loan programs allow as little as 3% down for first time buyers with good credit. These may have lower long-term costs than FHA loans because mortgage insurance can be removed once you reach 20% equity.</p>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="font-heading text-3xl font-bold mb-6">First Time Home Buyer Grants in NJ</h2>
            <p className="text-muted-foreground mb-4">Grants are free money that you don't have to pay back. Here are the main grant programs available to NJ first time buyers:</p>
            <ul className="list-disc pl-6 space-y-3 text-muted-foreground">
              <li><strong>NJHMFA Down Payment Assistance:</strong> Up to $15,000 as a forgivable loan (functions like a grant if you stay 5 years).</li>
              <li>Your local municipality may offer additional grants or tax abatements for first time buyers. Check with your town's housing department for local programs.</li>
              <li>Some employers offer home buying assistance programs. Ask your HR department about any homeownership benefits.</li>
            </ul>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="font-heading text-3xl font-bold mb-6">How Much House Can You Afford in South Jersey?</h2>
            <p className="text-muted-foreground mb-6">The general rule is that your total monthly housing payment (mortgage, taxes, insurance) shouldn't exceed 28-31% of your gross monthly income. In South Jersey, here's what that looks like in practice:</p>
            <div className="bg-muted/50 rounded-lg p-6 space-y-3 mb-6">
              <p className="text-foreground"><strong>Household income of $75,000/year:</strong> Approximately $250,000-$300,000 purchase price</p>
              <p className="text-foreground"><strong>Household income of $100,000/year:</strong> Approximately $350,000-$400,000 purchase price</p>
              <p className="text-foreground"><strong>Household income of $150,000/year:</strong> Approximately $500,000-$575,000 purchase price</p>
            </div>
            <p className="text-muted-foreground">Keep in mind that NJ property taxes are among the highest in the nation, which affects your purchasing power. Property taxes vary significantly by town — Moorestown might be $10,000-$15,000/year while Pennsauken might be $5,000-$8,000/year for a similar-priced home.</p>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="font-heading text-3xl font-bold mb-6">Closing Costs for First Time Buyers in NJ</h2>
            <p className="text-muted-foreground">
              First time buyers in NJ should budget 2-5% of the purchase price for closing costs. On a $350,000 home, that's $7,000-$17,500. Common closing costs include lender fees, title insurance, attorney fees, recording fees, and prepaid property taxes and homeowners insurance. See our complete{" "}
              <Link to="/closing-costs-nj" className="text-primary hover:underline font-medium">NJ Closing Costs Guide</Link>{" "}
              for a detailed breakdown.
            </p>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="font-heading text-3xl font-bold mb-6">Best South Jersey Towns for First Time Buyers</h2>
            <p className="text-muted-foreground mb-6">Looking for affordable communities with good schools and quality of life? Here are some of our top picks for first time buyers in South Jersey:</p>
            <div className="space-y-4">
              {bestTowns.map((town) => (
                <div key={town.slug} className="flex items-start gap-3">
                  <ArrowRight className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <p className="text-muted-foreground">
                    <Link to={`/${town.slug}`} className="text-primary hover:underline font-medium">{town.name}</Link> — {town.desc}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section>
            <h2 className="font-heading text-3xl font-bold mb-6">Frequently Asked Questions</h2>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <details key={i} className="group border border-border rounded-lg">
                  <summary className="flex items-center justify-between cursor-pointer p-4 font-medium text-foreground hover:bg-muted/50 rounded-lg">
                    {faq.q}
                    <span className="ml-2 transition-transform group-open:rotate-180">▼</span>
                  </summary>
                  <div className="px-4 pb-4 text-muted-foreground">{faq.a}</div>
                </details>
              ))}
            </div>
          </section>

          {/* Explore Communities */}
          <section>
            <h2 className="font-heading text-3xl font-bold mb-4">Explore South Jersey Communities</h2>
            <p className="text-muted-foreground mb-6">Ready to start your search? Browse homes for sale across South Jersey's most popular communities.</p>
            <div className="flex flex-wrap gap-2">
              {[
                { name: "Cherry Hill", path: "/cherry-hill-homes-for-sale" },
                { name: "Moorestown", path: "/moorestown-homes-for-sale" },
                { name: "Mount Laurel", path: "/mount-laurel-homes-for-sale" },
                { name: "Marlton", path: "/marlton-homes-for-sale" },
                { name: "Voorhees", path: "/voorhees-homes-for-sale" },
                { name: "Haddonfield", path: "/haddonfield-homes-for-sale" },
                { name: "Washington Township", path: "/washington-township-homes-for-sale" },
                { name: "Gloucester Township", path: "/gloucester-township-homes-for-sale" },
                { name: "Deptford", path: "/deptford-homes-for-sale" },
                { name: "Cinnaminson", path: "/cinnaminson-homes-for-sale" },
                { name: "Medford", path: "/medford-homes-for-sale" },
                { name: "Collingswood", path: "/collingswood-homes-for-sale" },
              ].map((c) => (
                <Link key={c.name} to={c.path} className="inline-flex items-center px-3 py-1.5 rounded-full border border-border text-sm text-primary hover:bg-primary/5 transition-colors">
                  {c.name}
                </Link>
              ))}
            </div>
          </section>

          {/* Agent Branding */}
          {isBranded && brandedAgent && (
            <section>
              <AgentBrandingCard agent={brandedAgent} />
            </section>
          )}

          {/* CTA */}
          <section className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl p-8 md:p-12 text-center">
            <h2 className="font-heading text-3xl font-bold mb-4">Ready to Buy Your First Home in South Jersey?</h2>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              {isBranded && brandedAgent
                ? `${brandedAgent.display_name || "Your agent"} guides first time buyers through every step. Reach out for personalized help.`
                : "Our experienced agents guide first time buyers through every step of the process. Get expert advice, local market knowledge, and access to every listing in South Jersey."}
            </p>
            <Button asChild size="lg">
              <Link to={isBranded && brandedAgent?.slug ? `/agent/${brandedAgent.slug}` : "/contact"}>
                {isBranded && brandedAgent ? `Contact ${brandedAgent.display_name?.split(" ")[0] || "Agent"}` : "Contact Us for a Free Buyer Consultation"}
              </Link>
            </Button>
          </section>
        </div>
      </article>
    </Layout>
  );
};

export default FirstTimeHomeBuyer;
